import { Typography } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { viewOriginalDocument } from '../../utils/documents/documentHelpers';
import { isSafeUrl, isExternalUrl, extractDocumentId } from '../../utils/security/linkSafety';
import { log } from '../../utils/errors/logger';

interface MarkdownMessageProps {
  content: string;
  isUser: boolean;
}

export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, isUser }) => {
  const { t } = useTranslation();

  const markdownComponents = {
    p: ({...props}: React.ComponentProps<'p'>) => (
      <p
        style={{
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          color: '#333333',
          margin: '0.5em 0'
        }}
        {...props}
      />
    ),
    a: ({href, children, ...props}: React.ComponentProps<'a'>) => {
      // SECURITY: Validate URL safety to prevent XSS attacks
      // Blocks: javascript:, data:, vbscript:, file: protocols
      // OWASP: A03:2021 - Injection
      if (!isSafeUrl(href)) {
        // Render as plain text if URL is unsafe
        log.warn('[MarkdownMessage] Rendering unsafe link as text', {
          href: href ? href.substring(0, 50) : 'undefined'
        });
        return <span style={{ color: '#666' }}>{children}</span>;
      }

      // Handle document links (format: document:documentId)
      if (href?.startsWith('document:')) {
        const documentId = extractDocumentId(href);

        // SECURITY: Validate document ID format to prevent injection
        if (!documentId) {
          log.warn('[MarkdownMessage] Invalid document ID format', {
            href: href.substring(0, 50)
          });
          return <span style={{ color: '#666' }}>{children}</span>;
        }

        return (
          <a
            style={{ color: '#003064', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              void viewOriginalDocument(documentId);
            }}
            role="button"
            tabIndex={0}
            aria-label={t('chat.sources.actions.viewDocument')}
          >
            {children || t('chat.sources.actions.viewDocument')}
          </a>
        );
      }

      // SECURITY: External links - add security attributes
      // rel="noopener noreferrer" prevents:
      // - window.opener access (tabnabbing attacks)
      // - referrer leakage
      if (isExternalUrl(href || '')) {
        return (
          <a
            style={{ color: '#003064', textDecoration: 'underline' }}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
            aria-label={typeof children === 'string' ? children : 'External link'}
          >
            {children || href}
          </a>
        );
      }

      // Internal links (relative URLs)
      return (
        <a
          style={{ color: '#003064', textDecoration: 'underline' }}
          href={href}
          {...props}
          aria-label={typeof children === 'string' ? children : 'Link'}
        >
          {children || href}
        </a>
      );
    }
  };
  if (isUser) {
    return (
      <Typography 
        variant="body1" 
        sx={{ 
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif', 
          color: '#333333'
        }}
      >
        {content}
      </Typography>
    );
  }

  return (
    <ReactMarkdown components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
};